
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { HydratedMatchGroup } from "../types";
import { compareLineItems } from "../utils";
import { DraggableLineItem } from './DraggableLineItem';

interface LineItemMatchGroupCardProps {
    group: HydratedMatchGroup;
    onApprove: () => void;
    onReject: () => void;
    roomName: string; // Used for "rematch" context or just visual debugging if needed
    selectedItemIds?: Set<number>;
    onAddSelectedItems?: (groupId: number) => void;
    canAddSelected?: boolean; // Whether selected items can be added to this group (prevents cross-room matching)
}

export function LineItemMatchGroupCard({ 
    group, 
    onApprove, 
    onReject, 
    selectedItemIds, 
    onAddSelectedItems,
    canAddSelected: canAddSelectedProp = true // Default to true for backwards compatibility
}: LineItemMatchGroupCardProps) {
    const { setNodeRef, isOver } = useDroppable({
        id: `match-group-${group.id}`,
        data: { type: 'match-group', groupId: group.id }
    });

    const isHighConfidence = group.confidence > 0.9;

    // Sort items by lineItemNumber for consistent display order
    const sortedInsurerItems = [...group.insurerItems].sort(compareLineItems);
    const sortedContractorItems = [...group.contractorItems].sort(compareLineItems);

    // Calculate totals
    const insurerTotal = group.insurerItems.reduce((sum, i) => sum + (i.rcvCents || 0), 0) / 100;
    const contractorTotal = group.contractorItems.reduce((sum, i) => sum + (i.rcvCents || 0), 0) / 100;
    const diff = contractorTotal - insurerTotal;

    const isApproved = group.status === 'approved' || group.status === 'user_confirmed';

    // Determine if we can show "Add Selected" button
    const hasSelection = selectedItemIds && selectedItemIds.size > 0;
    const canAddSelected = hasSelection && onAddSelectedItems && !isApproved && canAddSelectedProp;

    const handleAddSelected = () => {
        if (onAddSelectedItems && canAddSelected) {
            onAddSelectedItems(group.id);
        }
    };

    return (
        <Card
            ref={setNodeRef}
            onClick={canAddSelected ? handleAddSelected : undefined}
            className={cn(
                "overflow-hidden border-l-4 transition-all",
                isOver && "ring-2 ring-primary ring-offset-2 bg-primary/5",
                isApproved ? "border-l-blue-500 bg-blue-50/10" : "border-l-slate-200 hover:border-l-primary",
                canAddSelected && "cursor-pointer hover:ring-2 hover:ring-blue-500 hover:ring-offset-2 hover:bg-blue-50/20 hover:border-l-blue-500"
            )}
            title={canAddSelected ? "Click to add selected items to this group" : undefined}
        >
            <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">

                {/* Insurer Side - Left */}
                <div className="flex-1 flex flex-col p-3 gap-2 min-w-0">
                    <div className="flex-1 space-y-2">
                        {sortedInsurerItems.length > 0 ? (
                            sortedInsurerItems.map(item => (
                                <DraggableLineItem
                                    key={item.lineItemId}
                                    item={{ ...item, type: 'insurer' } as any}
                                    isSelected={false}
                                    onToggle={() => { }}
                                    isLocked={isApproved}
                                    showCheckbox={false}
                                />
                            ))
                        ) : (
                            <div className="h-full min-h-[48px] flex items-center justify-center border border-dashed rounded text-xs text-muted-foreground bg-slate-50">
                                Drop Insurer Item Here
                            </div>
                        )}
                    </div>
                    <div className="mt-auto pt-2 flex justify-between items-center px-1 border-t border-slate-200">
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">Total:</span>
                        <span className="text-xs font-medium">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(insurerTotal)}
                        </span>
                    </div>
                </div>

                {/* Meta & Actions - Center */}
                <div className="w-full md:w-40 bg-slate-50/50 dark:bg-slate-950/50 p-3 flex flex-row md:flex-col items-center justify-between md:justify-center gap-3 shrink-0">
                    <div className="flex flex-col items-center gap-1">
                        <Badge variant={isHighConfidence ? "outline" : "secondary"} className={cn(
                            "text-[10px] whitespace-nowrap",
                            isHighConfidence ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-100 text-amber-800"
                        )}>
                            {(group.confidence * 100).toFixed(0)}% Match
                        </Badge>

                        <div className={cn("text-xs font-bold mt-1",
                            diff > 0 ? "text-orange-600" :
                                diff < 0 ? "text-green-600" :
                                    "text-foreground"
                        )}>
                            {diff > 0 ? "+" : ""}{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(diff)}
                        </div>

                        {/* Visual indicator when items are selected */}
                        {canAddSelected && (
                            <div className="mt-2 text-[10px] text-blue-600 font-medium text-center animate-pulse">
                                Click to add
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={onReject} title="Reject / Delete Group">
                            <X className="h-4 w-4" />
                        </Button>

                        <Button
                            size="sm"
                            variant="ghost"
                            className={cn(
                                "h-7 w-7 p-0 transition-colors",
                                isApproved
                                    ? "bg-green-600 text-white hover:bg-green-700 cursor-default"
                                    : "text-muted-foreground hover:text-green-600 hover:bg-green-50"
                            )}
                            onClick={isApproved ? undefined : onApprove}
                            title={isApproved ? "Confirmed" : "Approve Match"}
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Contractor Side - Right */}
                <div className="flex-1 flex flex-col p-3 gap-2 min-w-0">
                    <div className="flex-1 space-y-2">
                        {sortedContractorItems.length > 0 ? (
                            sortedContractorItems.map(item => (
                                <DraggableLineItem
                                    key={item.lineItemId}
                                    item={{ ...item, type: 'contractor' } as any}
                                    isSelected={false}
                                    onToggle={() => { }}
                                    isLocked={isApproved}
                                    showCheckbox={false}
                                />
                            ))
                        ) : (
                            <div className="h-full min-h-[48px] flex items-center justify-center border border-dashed rounded text-xs text-muted-foreground bg-blue-50/50">
                                Drop Contractor Item Here
                            </div>
                        )}
                    </div>
                    <div className="mt-auto pt-2 flex justify-between items-center px-1 border-t border-blue-200">
                        <span className="text-[10px] text-muted-foreground uppercase font-bold">Total:</span>
                        <span className="text-xs font-medium">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(contractorTotal)}
                        </span>
                    </div>
                </div>

            </div>
        </Card >
    );
}
